import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { addDays, format, startOfWeek, addWeeks, subWeeks, isSameDay } from 'date-fns';
import es from 'date-fns/locale/es';

const API_BASE = (window.location.origin.replace(/:\d+$/, '')) + (window.location.port ? ':8000' : '') + '/api';

const diasSemana = ['LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB', 'DOM'];

const PASOS = ['Profesional', 'Servicio', 'Fecha y hora', 'Tus datos'];

export default function ReservaCliente() {
  const { slug } = useParams();
  const [clinica, setClinica] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Booking state
  const [paso, setPaso] = useState(0);
  const [profesionalId, setProfesionalId] = useState(null); // null = cualquier profesional
  const [tratamientoId, setTratamientoId] = useState(null);
  const [fecha, setFecha] = useState(new Date());
  const [hora, setHora] = useState(null);
  const [semana, setSemana] = useState([]);
  const [inicioSemana, setInicioSemana] = useState(startOfWeek(new Date(), { weekStartsOn: 1 }));
  const [horarios, setHorarios] = useState([]);
  const [loadingHorarios, setLoadingHorarios] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    nombre_paciente: '',
    rut: '',
    fecha_nacimiento: '',
    telefono: '',
    email: '',
    notas: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [mensaje, setMensaje] = useState(null);
  const [reservaExito, setReservaExito] = useState(null);

  // Fetch clinic info
  useEffect(() => {
    const url = slug
      ? `${API_BASE}/public/clinica/${slug}/`
      : `${API_BASE}/public/clinica/podoclinic/`; // fallback default
    fetch(url)
      .then(r => {
        if (!r.ok) throw new Error('Clínica no encontrada.');
        return r.json();
      })
      .then(data => {
        setClinica(data);
        setLoading(false);
      })
      .catch(e => {
        setError(e.message);
        setLoading(false);
      });
  }, [slug]);

  // Build week days
  useEffect(() => {
    const dias = Array.from({ length: 7 }, (_, i) => addDays(inicioSemana, i));
    setSemana(dias);
    if (!dias.some(d => isSameDay(d, fecha))) {
      setFecha(dias[0]);
    }
  }, [inicioSemana]);

  // Fetch available hours when fecha or profesional changes
  useEffect(() => {
    if (!fecha || !clinica) return;
    fetchHorarios();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha, profesionalId]);

  const fetchHorarios = async () => {
    setLoadingHorarios(true);
    setHora(null);
    try {
      const fechaStr = format(fecha, 'yyyy-MM-dd');
      const params = new URLSearchParams({ fecha: fechaStr });
      if (tratamientoId) params.set('tratamiento_id', tratamientoId);
      if (profesionalId) params.set('profesional_id', profesionalId);

      const res = await fetch(`${API_BASE}/public/disponibilidad/${clinica.slug}/?${params}`);
      const data = await res.json();
      setHorarios(data.horas_disponibles || []);
    } catch {
      setHorarios([]);
    } finally {
      setLoadingHorarios(false);
    }
  };

  const tratamientosDisponibles = () => {
    if (!clinica) return [];
    if (profesionalId) {
      const prof = clinica.profesionales.find(p => p.id === profesionalId);
      return prof ? prof.tratamientos : [];
    }
    return clinica.tratamientos_base || [];
  };

  const handleSiguiente = () => {
    if (paso === 0 && !profesionalId) return;
    if (paso === 1 && !tratamientoId) return;
    if (paso === 2 && !hora) return;
    setPaso(p => p + 1);
  };

  const handleAnterior = () => setPaso(p => p - 1);

  const handleReservaExito = () => {
    setReservaExito({ fecha: format(fecha, 'EEEE d \'de\' MMMM', { locale: es }), hora, tratamiento: tratamientoNombre() });
    setPaso(4);
  };

  const tratamientoNombre = () => {
    const lista = tratamientosDisponibles();
    const t = lista.find(x => x.id === tratamientoId);
    return t ? t.nombre : '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setMensaje(null);
    try {
      const body = {
        slug: clinica.slug,
        tratamiento_id: tratamientoId,
        fecha: format(fecha, 'yyyy-MM-dd'),
        hora,
        ...formData
      };
      if (profesionalId) body.profesional_id = profesionalId;

      const res = await fetch(`${API_BASE}/public/reservar/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      if (res.ok) {
        handleReservaExito();
      } else {
        const err = await res.json();
        setMensaje(err.error || 'Error al reservar. Intenta de nuevo.');
      }
    } catch {
      setMensaje('Error de conexión.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="text-center py-20 text-gray-500">Cargando...</div>;
  if (error) return <div className="text-center py-20 text-red-500">{error}</div>;

  const clinicaNombre = clinica?.nombre || 'Clínica';

  return (
    <div className="max-w-lg mx-auto py-8 px-4">
      <h1 className="text-2xl font-bold mb-2 text-center">{clinicaNombre}</h1>
      <p className="text-center text-gray-500 mb-6">Reserva tu hora</p>

      {/* Stepper */}
      {paso < 4 && (
        <div className="flex justify-center mb-8">
          {PASOS.map((nombre, i) => (
            <div key={i} className="flex items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                ${i === paso ? 'bg-blue-500 text-white' : i < paso ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'}`}>
                {i < paso ? '✓' : i + 1}
              </div>
              {i < PASOS.length - 1 && (
                <div className={`w-6 h-0.5 ${i < paso ? 'bg-green-500' : 'bg-gray-200'}`} />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Paso 0: Profesional */}
      {paso === 0 && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Selecciona un profesional</h2>
          <button
            className={`w-full text-left p-4 mb-3 rounded-lg border-2 transition-colors
              ${profesionalId === null ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
            onClick={() => { setProfesionalId(null); setTratamientoId(null); }}
          >
            <div className="font-medium">Cualquier profesional disponible</div>
            <div className="text-sm text-gray-500">Te asignamos el primero libre</div>
          </button>
          {clinica.profesionales.map(prof => (
            <button
              key={prof.id}
              className={`w-full text-left p-4 mb-3 rounded-lg border-2 transition-colors
                ${profesionalId === prof.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
              onClick={() => { setProfesionalId(prof.id); setTratamientoId(null); }}
            >
              <div className="font-medium">{prof.nombre}</div>
              {prof.titulo_profesional && <div className="text-sm text-gray-500">{prof.titulo_profesional}</div>}
              <div className="text-xs text-gray-400 mt-1">{prof.tratamientos.length} servicios</div>
            </button>
          ))}
          <div className="flex gap-3 mt-6">
            <button onClick={handleAnterior} className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-600">Atrás</button>
            <button onClick={handleSiguiente} disabled={!profesionalId} className="flex-1 py-3 rounded-lg bg-blue-500 text-white disabled:opacity-40">Siguiente</button>
          </div>
        </div>
      )}

      {/* Paso 1: Tratamiento */}
      {paso === 1 && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Selecciona un servicio</h2>
          {tratamientosDisponibles().length === 0 ? (
            <p className="text-gray-500 text-center py-8">No hay servicios disponibles para este profesional.</p>
          ) : (
            tratamientosDisponibles().map(t => (
              <button
                key={t.id}
                className={`w-full text-left p-4 mb-3 rounded-lg border-2 transition-colors
                  ${tratamientoId === t.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`}
                onClick={() => setTratamientoId(t.id)}
              >
                <div className="flex justify-between items-center">
                  <div>
                    <div className="font-medium">{t.nombre}</div>
                    {t.descripcion && <div className="text-sm text-gray-500">{t.descripcion}</div>}
                  </div>
                  <div className="text-right">
                    <div className="font-semibold text-blue-600">${Number(t.precio).toLocaleString('es-CL')}</div>
                    <div className="text-xs text-gray-400">{t.duracion_minutos} min</div>
                  </div>
                </div>
              </button>
            ))
          )}
          <div className="flex gap-3 mt-6">
            <button onClick={handleAnterior} className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-600">Atrás</button>
            <button onClick={handleSiguiente} disabled={!tratamientoId} className="flex-1 py-3 rounded-lg bg-blue-500 text-white disabled:opacity-40">Siguiente</button>
          </div>
        </div>
      )}

      {/* Paso 2: Fecha y hora */}
      {paso === 2 && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Selecciona fecha y hora</h2>

          {/* Week selector */}
          <div className="flex justify-center items-center mb-4 space-x-2">
            <button onClick={() => setInicioSemana(subWeeks(inicioSemana, 1))} className="text-2xl px-2 text-gray-500">&#60;</button>
            {semana.map((dia, idx) => (
              <button
                key={idx}
                className={`flex flex-col items-center px-2 py-2 rounded-lg transition-colors text-sm
                  ${isSameDay(dia, fecha) ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600'}`}
                onClick={() => setFecha(dia)}
              >
                <span className="text-xs font-medium">{diasSemana[idx]}</span>
                <span className="text-base font-bold">{format(dia, 'd')}</span>
              </button>
            ))}
            <button onClick={() => setInicioSemana(addWeeks(inicioSemana, 1))} className="text-2xl px-2 text-gray-500">&#62;</button>
          </div>

          <p className="text-sm text-gray-500 text-center mb-2">
            {format(fecha, 'EEEE d \'de\' MMMM', { locale: es })}
          </p>

          {/* Time slots */}
          <div className="flex flex-wrap gap-2 justify-center mt-4">
            {loadingHorarios ? (
              <span className="text-gray-400 py-4">Cargando horarios...</span>
            ) : horarios.length === 0 ? (
              <span className="text-gray-400 py-4">No hay horarios disponibles</span>
            ) : (
              horarios.map(h => (
                <button
                  key={h}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors
                    ${hora === h ? 'bg-blue-500 text-white' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'}`}
                  onClick={() => setHora(h)}
                >
                  {h}
                </button>
              ))
            )}
          </div>

          <div className="flex gap-3 mt-6">
            <button onClick={handleAnterior} className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-600">Atrás</button>
            <button onClick={handleSiguiente} disabled={!hora} className="flex-1 py-3 rounded-lg bg-blue-500 text-white disabled:opacity-40">Siguiente</button>
          </div>
        </div>
      )}

      {/* Paso 3: Datos personales */}
      {paso === 3 && (
        <div>
          <h2 className="text-lg font-semibold mb-4">Tus datos</h2>
          {/* Resumen */}
          <div className="bg-blue-50 rounded-lg p-3 mb-4 text-sm">
            <div><span className="font-medium">Servicio:</span> {tratamientoNombre()}</div>
            <div><span className="font-medium">Fecha:</span> {format(fecha, 'd/MM/yyyy')}</div>
            <div><span className="font-medium">Hora:</span> {hora}</div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Nombre completo *</label>
              <input type="text" required className="mt-1 block w-full rounded border-gray-300 border p-2"
                value={formData.nombre_paciente}
                onChange={e => setFormData({ ...formData, nombre_paciente: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">RUT (sin puntos, con guión) *</label>
              <input type="text" required placeholder="12345678-9" className="mt-1 block w-full rounded border-gray-300 border p-2"
                value={formData.rut}
                onChange={e => setFormData({ ...formData, rut: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Fecha de nacimiento *</label>
              <input type="date" required className="mt-1 block w-full rounded border-gray-300 border p-2"
                value={formData.fecha_nacimiento}
                onChange={e => setFormData({ ...formData, fecha_nacimiento: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Teléfono *</label>
              <input type="tel" required className="mt-1 block w-full rounded border-gray-300 border p-2"
                value={formData.telefono}
                onChange={e => setFormData({ ...formData, telefono: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Correo electrónico *</label>
              <input type="email" required className="mt-1 block w-full rounded border-gray-300 border p-2"
                value={formData.email}
                onChange={e => setFormData({ ...formData, email: e.target.value })} />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Notas (opcional)</label>
              <textarea className="mt-1 block w-full rounded border-gray-300 border p-2"
                value={formData.notas}
                onChange={e => setFormData({ ...formData, notas: e.target.value })} />
            </div>

            {mensaje && <div className="text-red-500 text-sm text-center">{mensaje}</div>}

            <div className="flex gap-3 mt-4">
              <button type="button" onClick={handleAnterior} className="flex-1 py-3 rounded-lg border border-gray-300 text-gray-600">Atrás</button>
              <button type="submit" disabled={submitting}
                className="flex-1 py-3 rounded-lg bg-green-500 text-white disabled:opacity-50 font-semibold">
                {submitting ? 'Reservando...' : 'Confirmar Reserva'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Paso 4: Éxito */}
      {paso === 4 && reservaExito && (
        <div className="text-center py-8">
          <div className="text-5xl mb-4">✓</div>
          <h2 className="text-2xl font-bold text-green-600 mb-2">¡Reserva confirmada!</h2>
          <p className="text-gray-600 mb-4">
            Tu hora fue reservada exitosamente.<br />
            Te enviaremos un comprobante por correo.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left text-sm space-y-1">
            <div><span className="font-medium">Servicio:</span> {reservaExito.tratamiento}</div>
            <div><span className="font-medium">Fecha:</span> {reservaExito.fecha}</div>
            <div><span className="font-medium">Hora:</span> {reservaExito.hora}</div>
          </div>
          <button onClick={() => window.location.reload()} className="mt-6 px-6 py-3 bg-blue-500 text-white rounded-lg font-medium">
            Realizar otra reserva
          </button>
        </div>
      )}
    </div>
  );
}
